import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPinOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/dateFormat";
import { useI18n } from "@/context/I18nContext";

const AssignRow = ({ u, munis, onDone, t }) => {
  const [mid, setMid] = useState(u.candidates[0]?.id || "");
  const assign = async () => {
    try {
      await api.post(`/admin/users/${u.id}/assign`, { municipality_id: mid });
      toast.success(t("user_assigned_toast"));
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  return (
    <li className="space-y-2 py-3" data-testid={`pending-assignment-${u.id}`}>
      <div className="font-medium">{u.name} <span className="text-xs text-muted-foreground">· {u.email}</span></div>
      <div className="text-sm text-muted-foreground">{u.address} {u.created_at && `· ${formatDateTime(u.created_at)}`}</div>
      <div className="text-xs text-muted-foreground">
        {u.candidates.length ? `${t("candidates")}: ${u.candidates.map((c) => c.name).join(", ")}` : t("no_candidate")}
      </div>
      <div className="flex flex-wrap gap-2">
        <Select value={mid} onValueChange={setMid}>
          <SelectTrigger className="w-full sm:w-72" data-testid={`assign-muni-select-${u.id}`}>
            <SelectValue placeholder={t("select_municipality")} />
          </SelectTrigger>
          <SelectContent>
            {munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={assign} disabled={!mid} data-testid={`assign-user-btn-${u.id}`}>{t("assign")}</Button>
      </div>
    </li>
  );
};

export const PendingAssignmentsPanel = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [munis, setMunis] = useState([]);
  const load = () => api.get("/admin/pending-assignments").then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/municipalities").then((r) => setMunis(r.data)).catch(() => {});
  }, []);

  return (
    <Card data-testid="pending-assignments-panel">
      <CardContent className="space-y-3 p-5">
        <div className="flex items-center gap-2">
          <MapPinOff className="h-4 w-4 text-primary" />
          <h3 className="font-semibold">{t("pending_assignments_title")} ({items.length})</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t("pending_assignments_desc")}</p>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="pending-assignments-empty">{t("pending_assignments_empty")}</p>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((u) => <AssignRow key={u.id} u={u} munis={munis} onDone={load} t={t} />)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
